import Backbone from 'backbone';
import AdminConstants from 'constants/AdminConstants';
import AdminAppDispatcher from 'dispatcher/AdminAppDispatcher';
import ToastStore from 'stores/ToastStore';
import CustomersActionCreators from 'actions/CustomersActionCreators';
import ObjectAssign from 'object-assign';
import { EventEmitter } from 'events';
import i18next from 'i18next';

// Shorcut to display messages to the user
function toast(message, level) {
  if (!level) level = 'error';

  ToastStore.getNotifications().push({
    title: message,
    body: '',
    level: level,
  });

  ToastStore.emitChange();
}

const QUOTE_RECEIVED = 'QUOTE_RECEIVED';
const QUOTES_RECEIVED = 'QUOTES_RECEIVED';

const quoteRequestInitialState = {
  pending: false,
  complete: false,
  success: false,
  error: null,
};

const storeProto = ObjectAssign({}, EventEmitter.prototype, {
  quotes: [],
  quoteRequestState: {
    ...quoteRequestInitialState,
  },
  selectedQuote: null,
  defaults: {
    loading: true,
    details: undefined,
    carriers: false,
    shippers: false,
    shipper: false,
    status: false,
    error: false,
    assignedShipperId: false,
    quotes: [],
    quoteRequestState: {
      ...quoteRequestInitialState,
    },
    selectedQuote: null,
  },
  reset() {
    this.status = false;
    this.error = false;
    this.assignedShipperId = false;
    this.unassignedShipperId = false;
    this.quotes = [];
    this.quoteRequestState = {
      ...quoteRequestInitialState,
    };
  },
  getStatus() {
    return this.status;
  },

  getStatusCode() {
    return this.statusCode;
  },

  setStatusCode(statusCode) {
    this.statusCode = statusCode;
  },

  getError() {
    return this.error;
  },
  getShippers() {
    return this.shippers;
  },
  getShipper() {
    return this.shipper;
  },
  getUpdatedShipper() {
    return this.details;
  },
  getAssignedShipperId() {
    return this.assignedShipperId;
  },
  getDetails() {
    return this.details;
  },
  clearUpdatedShipper() {
    this.details = undefined;
  },
  clearShippers() {
    this.shippers = [];
  },
  getShippersCount() {
    return this.count;
  },
  getShipperQuotes() {
    return {
      total_entries: this.total_entries,
      pages: this.pages,
      quotes: this.quotes,
    };
  },
  getQuoteRequestState() {
    return this.quoteRequestState;
  },
  getQuote() {
    return this.selectedQuote;
  },

  addChangeListener(callback) {
    this.on('change', callback);
  },

  addQuotesChangeListener(callback) {
    this.on(QUOTES_RECEIVED, callback);
  },
  addQuoteChangeListener(callback) {
    this.on(QUOTE_RECEIVED, callback);
  },

  removeChangeListener(callback) {
    this.removeListener('change', callback);
  },

  removeQuotesChangeListener(callback) {
    this.removeListener(QUOTES_RECEIVED, callback);
  },

  removeQuoteChangeListener(callback) {
    this.removeListener(QUOTE_RECEIVED, callback);
  },

  emitQuoteChange() {
    this.emit(QUOTE_RECEIVED);
  },
  emitChange() {
    this.emit('change');
  },
  emitQuotesChange() {
    this.emit(QUOTES_RECEIVED);
  },

  onFetchQuoteRequest() {
    this.quoteRequestState = {
      ...this.quoteRequestState,
      pending: true,
      complete: false,
      success: false,
      error: null,
    };
    this.emitQuoteChange();
  },

  onFetchQuoteSuccess(selectedQuote) {
    this.quoteRequestState = {
      ...this.quoteRequestState,
      pending: false,
      complete: true,
      success: true,
      error: null,
    };
    this.selectedQuote = selectedQuote;
    this.emitQuoteChange();
  },

  onFetchQuoteFail(errorMessage) {
    this.quoteRequest = {
      ...this.quoteRequest,
      pending: false,
      complete: true,
      success: false,
      error: errorMessage,
    };
    this.emitQuoteChange();
  },
});

const Actions = AdminConstants.ActionTypes;

// Action handlers
storeProto[Actions.CUSTOMER_FETCH] = function () {
  this.loading = true;
};

storeProto[Actions.RECEIVE_CUSTOMER] = function (payload) {
  const customer = payload.shipper || payload.carrier;
  this.status = 'success';
  this.loading = false;
  this.details = customer;
  this.emitChange();
};

storeProto[Actions.UPDATE_CUSTOMER] = function (payload) {
  const customer = payload.shipper || payload.carrier;
  const details = ObjectAssign({}, this.details, customer);
  this.status = 'success';
  this.loading = false;
  this.details = details;
  this.emitChange();
};

storeProto[Actions.RECEIVE_CARRIERS] = function (payload) {
  this.status = 'success';
  this.carriers = payload.carriers;
  this.emitChange();
};

storeProto[Actions.RECEIVE_CARRIER_RMIS] = function (payload) {
  this.status = 'success';
  this.carriers.rmis = payload;
  this.emitChange();
};

storeProto[Actions.RECEIVE_SHIPPERS] = function (payload) {
  this.status = 'success';
  this.shippers = payload.shippers;
  this.count = payload.count;
  this.emitChange();
};

storeProto[Actions.RECEIVE_SHIPPER] = function (payload) {
  this.status = 'success';
  this.shipper = payload.shipper;
  this.emitChange();
};

storeProto[Actions.RECEIVE_SHIPPER_QUOTES] = function (payload) {
  const { items: quotes, total_entries, pages } = payload.result;
  this.status = 'success';
  this.quotes = (quotes || []).map(addAccessorials);
  this.pages = pages || 1;
  this.total_entries = total_entries || quotes.length;
  this.emitQuotesChange();
};

storeProto[Actions.FETCH_QUOTE_AFTER_UPDATE_SUCCESS] = function (payload) {
  const { quote } = payload.result;
  this.status = 'success';
  this.quotes = (quote ? [quote] : []).map(addAccessorials);
  this.emitQuotesChange();
};

storeProto[Actions.FETCH_QUOTE_REQUEST] = function () {
  this.onFetchQuoteRequest();
};

storeProto[Actions.FETCH_QUOTE_SUCCESS] = function (payload) {
  this.status = 'success';
  this.onFetchQuoteSuccess(payload.quote);
};

storeProto[Actions.FETCH_QUOTE_FAIL] = function (errorMessage) {
  this.status = 'failure';
  this.onFetchQuoteFail(errorMessage);
};

storeProto[Actions.CREATE_QUOTE_SUCCESS] = function (payload) {
  this.status = 'success';
  this.quotes = [addAccessorials(payload.quote), ...this.quotes];
  this.emitChange();
};

storeProto[Actions.UPDATED_CARRIER] = function (payload) {
  this.status = 'success';
  this.details = Object.assign({}, this.details, payload.carrier); // I have no idea what the broader implications of this are
  this.emitChange();
};

storeProto[Actions.UPDATE_SHIPPER_FAIL] = function (payload) {
  this.setStatusCode(payload.data.status.code);
  this.status = 'fail';
  this.error = payload.data.entity;
  this.emitChange();
};
storeProto[Actions.UPDATE_ADMIN_FAIL] = function (payload) {
  this.setStatusCode(payload.data.status.code);
  this.status = 'fail';
  this.error = payload.data.entity;
  this.emitChange();
};
storeProto[Actions.UPDATE_ADMIN_SUCCESS] = function (payload) {
  this.setStatusCode(200);
  this.status = 'success';
  this.emitChange();
};
storeProto[Actions.UPDATED_SHIPPER] = function (payload) {
  this.setStatusCode(200);
  this.status = 'success';
  const next = { ...payload.shipper };
  // Server PATCH /api/1/shippers/:id does not populate origin/destination.
  // If the previously-loaded details already had them populated and the
  // server is returning the same _id back as a bare ObjectId, keep the
  // populated copy so LocationPreview can still render name/address.
  ['origin', 'destination'].forEach((field) => {
    const incoming = next[field];
    const existing = this.details && this.details[field];
    const incomingIsId = incoming && typeof incoming !== 'object';
    const existingIsPopulated =
      existing && typeof existing === 'object' && existing._id;
    if (
      incomingIsId &&
      existingIsPopulated &&
      String(existing._id) === String(incoming)
    ) {
      next[field] = existing;
    }
  });
  this.details = next;
  this.emitChange();
};

storeProto[Actions.UPDATED_VENDOR] = function (payload) {
  this.setStatusCode(200);
  this.status = 'success';
  this.details = payload.vendor;
  this.emitChange();
};

storeProto[Actions.ASSIGN_TARIFF_SUCCESS] = function (payload) {
  this.status = 'success';
  this.assignedShipperId = payload.data.shipper_id;
  this.emitChange();
  toast(i18next.t('TARIFF_ASSIGN_SUCCESS'), 'success');
};

storeProto[Actions.ASSIGN_TARIFF_FAIL] = function (payload) {
  this.status = 'fail';
  this.error = payload.data.entity;
  this.emitChange();
  toast(i18next.t('TARIFF_ASSIGN_FAILED'), 'fail');
};

storeProto[Actions.UNASSIGN_TARIFF_SUCCESS] = function (payload) {
  this.status = 'success';
  this.assignedShipperId = payload.data.shipper_id;
  this.emitChange();
  toast(i18next.t('TARIFF_UNASSIGN_SUCCESS'), 'success');
};

storeProto[Actions.UNASSIGN_TARIFF_FAIL] = function (payload) {
  this.status = 'fail';
  this.error = payload.data.entity;
  this.emitChange();
  toast(i18next.t('TARIFF_UNASSIGN_FAILED'), 'fail');
};

storeProto[Actions.UPDATED_DRIVER] = function (payload) {
  const driver = payload.driver;
  const details = ObjectAssign({}, this.get('details'));

  if (details._type === 'Carrier') {
    for (let i = details.drivers.length - 1; i >= 0; i -= 1) {
      if (details.drivers[i]._id === driver._id) {
        details.drivers[i] = driver;
      }
    }
    this.status = 'success';
    this.details = details;
  } else {
    this.status = 'success';
    this.details = driver;
  }
};

storeProto[Actions.CREATED_DRIVER] = function (payload) {
  if (this.details._type === 'Carrier') {
    this.details.drivers = (this.details.drivers || []).concat([
      payload.driver,
    ]);
    this.status = 'success';
    this.emitChange();
  }
};

storeProto[Actions.UPDATED_CREDIT_CARD] = function (payload) {
  CustomersActionCreators.updatedShipperBilling(payload.data[0].customer);
  const details = ObjectAssign({}, this.get('details'));
  const userId = payload.id;
  const data = payload.data[0];
  const card = details.funding_sources && details.funding_sources[0];

  if (details._id === userId && (!card || card.id !== data.id)) {
    details.funding_sources = [data];
    this.status = 'success';
    this.details = details;
    toast(i18next.t('CREDIT_CARD_UPDATED'), 'success');
  }
};

function isBillingUpdated(userId, details, payload) {
  return (
    details._id === userId &&
    ((details.bank_accounts &&
      payload.bank_accounts.length !== details.bank_accounts.length) ||
      (details.bank_accounts[0] &&
        payload.bank_accounts[0].id !== details.bank_accounts[0].id) ||
      !details.stripe_verification ||
      details.stripe_verification.status !== payload.stripe_verification.status)
  );
}
storeProto[Actions.UPDATED_BANK_ACCOUNT] = function (payload) {
  const details = ObjectAssign({}, this.get('details'));
  const update = payload.data;

  if (isBillingUpdated(payload.id, details, update)) {
    this.status = 'success';
    this.details = ObjectAssign(details, update);
    toast(i18next.t('BILLING_INFO_UPDATED'), 'success');
  }
};

function onDispatchEnd(clbk) {
  if (AdminAppDispatcher.isDispatching()) {
    setTimeout(onDispatchEnd.bind(null, clbk), 100);
  } else {
    clbk();
  }
}
storeProto[Actions.CREATED_ORIGIN] = function (payload) {
  const originData = payload.data;
  // Optimistically apply the populated Location locally so LocationPreview
  // shows the new name/address immediately, without waiting for the shipper
  // PATCH roundtrip (which returns origin as a bare ObjectId).
  this.details = { ...this.details, origin: originData };
  this.emitChange();

  const update = {
    _id: this.details._id,
    origin: originData._id,
  };

  onDispatchEnd(
    CustomersActionCreators['update' + this.details._type].bind(
      CustomersActionCreators,
      update,
    ),
  );
};

storeProto[Actions.CREATED_DESTINATION] = function (payload) {
  const destinationData = payload.data;
  this.details = { ...this.details, destination: destinationData };
  this.emitChange();

  const update = {
    _id: this.details._id,
    destination: destinationData._id,
  };
  onDispatchEnd(
    CustomersActionCreators['update' + this.details._type].bind(
      CustomersActionCreators,
      update,
    ),
  );
};
const UserDetailsStore = Backbone.Model.extend(storeProto);
const store = new UserDetailsStore();

// Register action handlers
AdminAppDispatcher.register(function ({ action }) {
  if (store[action.type]) {
    store[action.type](action);
  }
});

function addAccessorials(quote) {
  return {
    accessorials: {},
    ...quote,
  };
}

export default store;
